// Mi Cuenta — perfil de la cuenta/tenant.
//
// Permite configurar el AVATAR de la cuenta: subir una imagen desde la galería
// del dispositivo que reemplaza la letra inicial que se muestra en la cuenta.
// Flujo de subida (igual que las fotos de disco): 1) URL prefirmada, 2) PUT
// directo a S3, 3) fijar la clave como avatar. Al guardar/quitar se recargan
// los permisos para que el nuevo avatar se refleje en toda la app.

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  AlertMessage,
  AsyncBoundary,
  ButtonSpinner,
  Card,
  Icon,
  PageHeader,
} from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import { useToast } from '../ui/ToastProvider.js';
import { ConfirmDialog } from '../ui/Modal.js';
import { LanguageSelector } from '../ui/LanguageSelector.js';
import { downloadBase64 } from '../ui/download.js';
import { openCookiePreferences } from '../consent/CookieBanner.js';

/** Tipos de imagen aceptados (coinciden con la lista blanca del backend). */
const ACCEPTED = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif';
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

/**
 * Palabra EXACTA que el usuario debe teclear para confirmar el borrado. Debe
 * coincidir con la que valida el backend (DELETE_ACCOUNT_CONFIRM_PHRASE); por eso
 * es un literal compartido del contrato, no un texto traducible.
 */
const DELETE_CONFIRM_WORD = 'ELIMINAR';

export function AccountView() {
  const { t } = useTranslation();
  const api = useApi();
  const toast = useToast();
  const { permissions, reloadPermissions, isOwner } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const profile = useAsync(() => api.account.getProfile(), []);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Confirmación de quitar avatar (reemplaza el `confirm()` nativo).
  const [confirmRemoveAvatar, setConfirmRemoveAvatar] = useState(false);

  const displayName = permissions?.displayName ?? permissions?.email ?? t('account.defaultUser');
  const initial = (displayName || 'U').trim().charAt(0).toUpperCase();
  const avatarUrl = permissions?.avatarUrl ?? null;

  async function onFileSelected(file: File) {
    if (!file.type.startsWith('image/')) {
      setError(t('account.errSelectImage'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('account.errTooLarge'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const contentType = file.type || 'image/jpeg';
      // 1) URL prefirmada. 2) PUT directo a S3. 3) fijar como avatar.
      const { upload_url, s3_key } = await api.account.avatarUploadUrl(contentType);
      const put = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'content-type': contentType },
        body: file,
      });
      if (!put.ok) throw new Error(t('account.errUploadS3'));
      await api.account.setAvatar(s3_key);
      await reloadPermissions();
      toast.success(t('account.msgAvatarUpdated'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account.errUpdateAvatar'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function removeAvatar() {
    setConfirmRemoveAvatar(false);
    setBusy(true);
    setError(null);
    try {
      await api.account.removeAvatar();
      await reloadPermissions();
      toast.success(t('account.msgAvatarRemoved'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account.errRemoveAvatar'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader title={t('account.pageTitle')} subtitle={t('account.pageSubtitle')} back />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />

        {/* Nombre del catálogo (tenant). */}
        <AsyncBoundary state={profile}>
          {(data) => (
            <CatalogNameCard
              initialName={data.tenant_name ?? ''}
              onError={setError}
              onSaved={() => {
                toast.success(t('account.msgCatalogUpdated'));
                profile.reload();
              }}
            />
          )}
        </AsyncBoundary>

        <Card title={t('account.avatarTitle')} icon="account_circle">
          <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
            {t('account.avatarHint')}
          </p>

          <div className="flex items-center gap-space-md">
            {/* Vista previa: imagen o inicial. */}
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={t('account.avatarAlt')}
                className="w-20 h-20 shrink-0 rounded-full object-cover border border-primary-container/40"
              />
            ) : (
              <span className="w-20 h-20 shrink-0 rounded-full bg-primary-container/20 border border-primary-container/40 text-primary flex items-center justify-center font-headline-md text-headline-md font-bold">
                {initial}
              </span>
            )}

            <div className="flex flex-col gap-space-xs">
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                {busy ? <ButtonSpinner /> : <Icon name="upload" className="text-[16px]" />}
                {avatarUrl ? t('account.changeImage') : t('account.uploadImage')}
              </button>
              {avatarUrl && (
                <button
                  type="button"
                  className="btn-secondary btn-sm !text-priority-urgent"
                  disabled={busy}
                  onClick={() => setConfirmRemoveAvatar(true)}
                >
                  <Icon name="delete" className="text-[16px]" /> {t('account.removeAvatar')}
                </button>
              )}
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFileSelected(file);
            }}
          />
        </Card>

        {/* Idioma de la aplicación (i18n). Se guarda en la cuenta y prevalece en
            el próximo inicio de sesión. */}
        <Card title={t('account.languageTitle')} icon="language">
          <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
            {t('account.languageSubtitle')}
          </p>
          <LanguageSelector variant="block" />
        </Card>

        {/* Privacidad y datos: comprobante de consentimiento, configuración de
            cookies y (más abajo) la zona peligrosa de borrado. */}
        <PrivacyDataCard />

        {/* Zona peligrosa: ELIMINAR la cuenta por completo. Solo el propietario
            (el backend reafirma ownerOnly; ocultarlo aquí es solo UX). */}
        {isOwner() && <DeleteAccountCard />}
      </div>

      {/* Confirmación de quitar avatar (reemplaza el confirm() nativo). */}
      <ConfirmDialog
        open={confirmRemoveAvatar}
        onConfirm={() => void removeAvatar()}
        onCancel={() => setConfirmRemoveAvatar(false)}
        title={t('account.removeAvatar')}
        message={t('account.removeAvatarConfirm')}
        confirmLabel={t('account.removeAvatar')}
        tone="danger"
        icon="delete"
      />
    </div>
  );
}

/**
 * Tarjeta de PRIVACIDAD Y DATOS: acciones de autoservicio del usuario sobre sus
 * datos personales — descargar el comprobante de consentimiento (PDF) y abrir la
 * configuración de cookies. El export de datos se añade en su propia tarjeta.
 */
function PrivacyDataCard() {
  const { t } = useTranslation();
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function downloadReceipt() {
    setBusy(true);
    setError(null);
    try {
      const receipt = await api.me.consentReceipt();
      downloadBase64(receipt.base64, receipt.filename, receipt.content_type);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  async function exportData() {
    setExporting(true);
    setError(null);
    try {
      const pkg = await api.me.dataExport();
      downloadBase64(pkg.base64, pkg.filename, pkg.content_type);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setExporting(false);
    }
  }

  return (
    <Card title={t('account.privacyTitle')} icon="privacy_tip">
      <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
        {t('account.privacySubtitle')}
      </p>
      <AlertMessage message={error} className="mb-space-sm" />
      <div className="flex flex-col gap-space-sm">
        <button
          type="button"
          className="btn-secondary w-full justify-center"
          disabled={busy}
          onClick={() => void downloadReceipt()}
        >
          {busy ? <ButtonSpinner /> : <Icon name="receipt_long" className="text-[18px]" />}{' '}
          {t('account.downloadConsentReceipt')}
        </button>
        <button
          type="button"
          className="btn-secondary w-full justify-center"
          disabled={exporting}
          onClick={() => void exportData()}
        >
          {exporting ? <ButtonSpinner /> : <Icon name="download" className="text-[18px]" />}{' '}
          {exporting ? t('account.exportingData') : t('account.exportMyData')}
        </button>
        <button
          type="button"
          className="btn-secondary w-full justify-center"
          onClick={() => openCookiePreferences()}
        >
          <Icon name="cookie" className="text-[18px]" /> {t('account.cookieSettings')}
        </button>
      </div>
    </Card>
  );
}

/**
 * Tarjeta de ELIMINACIÓN TOTAL de la cuenta (solo propietario). Replica el patrón
 * de doble confirmación de la "zona peligrosa" del catálogo: 1) revelar, 2)
 * teclear la palabra exacta. Al confirmar, llama a DELETE /account; si tenía plan
 * de pago, el backend lo cancela antes. Tras borrar, cierra sesión y vuelve al
 * inicio (la cuenta ya no existe).
 */
function DeleteAccountCard() {
  const { t } = useTranslation();
  const api = useApi();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Diálogo de última confirmación tras teclear la palabra exacta.
  const [confirmFinal, setConfirmFinal] = useState(false);

  async function doDelete() {
    setConfirmFinal(false);
    setBusy(true);
    setError(null);
    try {
      // Envía la palabra EXACTA que exige el backend (contrato), no el texto
      // tecleado (que ya validamos que coincide con DELETE_CONFIRM_WORD).
      await api.account.deleteAccount(DELETE_CONFIRM_WORD);
      // La cuenta ya no existe: cerrar sesión y volver al inicio público.
      await signOut();
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account.errDelete'));
      setBusy(false);
    }
  }

  return (
    <Card title={t('account.deleteTitle')} icon="warning">
      <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
        {t('account.deleteHint')}
      </p>
      <AlertMessage message={error} className="mb-space-sm" />

      {!open ? (
        <button
          className="btn-ghost !text-priority-urgent border border-error/40 w-full"
          type="button"
          onClick={() => setOpen(true)}
        >
          <Icon name="delete_forever" className="text-[18px]" /> {t('account.deleteButton')}
        </button>
      ) : (
        <div className="flex flex-col gap-space-sm p-space-sm rounded-lg border border-error/40 bg-error-container/10">
          <p className="font-body-sm text-body-sm text-error">
            {t('account.deleteConfirmLead', { word: DELETE_CONFIRM_WORD })}
          </p>
          <div className="field-slot">
            <input
              className="field-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={DELETE_CONFIRM_WORD}
              autoComplete="off"
              aria-label={t('account.deleteConfirmLead', { word: DELETE_CONFIRM_WORD })}
            />
          </div>
          <div className="flex items-center gap-space-sm">
            <button
              className="btn-secondary"
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setText('');
              }}
            >
              {t('account.cancel')}
            </button>
            <button
              className="btn-ghost !text-priority-urgent border border-error/40 flex-1"
              type="button"
              disabled={busy || text.trim() !== DELETE_CONFIRM_WORD}
              onClick={() => setConfirmFinal(true)}
            >
              {busy ? <ButtonSpinner /> : <Icon name="delete_forever" className="text-[18px]" />}{' '}
              {busy ? t('account.deleting') : t('account.confirmDelete')}
            </button>
          </div>
        </div>
      )}

      {/* Última barrera: diálogo modal con foco atrapado antes del borrado real. */}
      <ConfirmDialog
        open={confirmFinal}
        onConfirm={() => void doDelete()}
        onCancel={() => setConfirmFinal(false)}
        title={t('account.deleteTitle')}
        message={t('account.deleteHint')}
        confirmLabel={t('account.confirmDelete')}
        tone="danger"
        busy={busy}
        icon="delete_forever"
      />
    </Card>
  );
}

/** Tarjeta para ver y editar el nombre del catálogo (tenant) de la propia cuenta. */
function CatalogNameCard({
  initialName,
  onError,
  onSaved,
}: {
  initialName: string;
  onError: (m: string | null) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '' || trimmed === initialName.trim()) return;
    setBusy(true);
    onError(null);
    try {
      await api.account.setTenantName(trimmed);
      onSaved();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('account.errUpdateCatalogName'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('account.catalogNameTitle')} icon="library_music">
      <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
        {t('account.catalogNameHint')}
      </p>
      <form onSubmit={save} className="flex flex-col gap-space-sm">
        <div className="field-slot">
          <Icon name="library_music" className="text-on-surface-variant text-[18px] mr-space-xs" />
          <input
            className="field-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('account.catalogNamePlaceholder')}
            aria-label={t('account.catalogNamePlaceholder')}
          />
        </div>
        <button
          className="btn-primary btn-sm self-start"
          type="submit"
          disabled={busy || name.trim() === '' || name.trim() === initialName.trim()}
        >
          {busy ? <ButtonSpinner /> : <Icon name="check" className="text-[16px]" />}{' '}
          {t('account.save')}
        </button>
      </form>
    </Card>
  );
}
